import { CheckCircle, Sort } from '@mui/icons-material'
import {
  Badge,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
} from '@mui/material'
import dayjs from 'dayjs'
import { get, isEmpty, map, toUpper } from 'lodash'
import { useContext, useEffect, useMemo, useState } from 'react'
import {
  approveRoleChangeRequest,
  declineRoleChangeRequest,
  getPendingRoleChangeRequests,
  getRoleChangeRequestFilterOptions,
  getRoleChangeRequestHistory,
} from '../../api/api'
import { FilterOptions, PendingRequest, ProcessedRequest, RequestHistoryFilters } from '../../api/models'
import DeclineIcon from '../../assets/cancel.svg'
import DeclinedIcon from '../../assets/declined.svg'
import EmptyBox from '../../assets/empty_box.svg'
import FetchError from '../../assets/fetch_error.svg'
import AcceptIcon from '../../assets/green_check.svg'
import { CustomTable } from '../../components/CustomTable'
import CustomDialog from '../../components/dialogs/CustomDialog/CustomDialog'
import { AppStateContext } from '../../state/AppProvider'
import styles from './AccessRequestManagement.module.css'
import FiltersDialog from './FiltersDialog/FiltersDialog'

const PAGE_SIZE = 10

type RoleChangeRequest = PendingRequest | ProcessedRequest

const AccessRequestManagement = () => {
  const [page, setPage] = useState<number>(1)
  const [data, setData] = useState<RoleChangeRequest[]>([])
  const [total, setTotal] = useState<number>(0)
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState<boolean>(false)
  const [isDeclineDialogOpen, setIsDeclineDialogOpen] = useState<boolean>(false)
  const [selectedRequest, setSelectedRequest] = useState<RoleChangeRequest | null>(null)
  const appStateContext = useContext(AppStateContext)
  const [declinedNotes, setDeclinedNotes] = useState<string>('')
  const [hasFetchError, setHasFetchError] = useState<boolean>(false)
  const [currentTab, setCurrentTab] = useState<'pending' | 'history'>('pending')
  const [isFiltersDialogOpen, setIsFiltersDialogOpen] = useState<boolean>(false)
  const filters = appStateContext?.state.roleChangeRequestFilterState
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null)
  const [ou, setOu] = useState<string>(appStateContext?.state.user?.ou ?? '')

  const headTitles =
    currentTab === 'pending'
      ? ['User', 'OU', 'Function', 'Reason', 'Requested', 'Actions']
      : ['User', 'Reason', 'Status', 'Notes', 'Processed by', 'Date']

  const getTotalPages = () => {
    return Math.ceil(total / PAGE_SIZE)
  }

  const fetchData = async (page: number, filtersFromProps: object = {}) => {
    if (currentTab === 'pending') {
      await getPendingRoleChangeRequests(page, PAGE_SIZE, ou)
        .then(res => {
          if (res) {
            setData(res.requests ?? [])
            setTotal(res.total ?? 0)
            setHasFetchError(false)
            setPage(page)
          } else {
            setHasFetchError(true)
          }
        })
        .catch(() => {
          setHasFetchError(true)
        })
    } else {
      await getRoleChangeRequestHistory({ page: page, page_size: PAGE_SIZE, ...filtersFromProps })
        .then(res => {
          if (res) {
            setData(res.requests ?? [])
            setTotal(res.total ?? 0)
            setHasFetchError(false)
            setPage(page)
            appStateContext?.dispatch({
              type: 'SET_ROLE_CHANGE_REQUEST_FILTER_STATE',
              payload: {
                status: get(filtersFromProps, 'status'),
                processed_by_user_id: get(filtersFromProps, 'processed_by_user_id'),
                start_date: get(filtersFromProps, 'start_date'),
                end_date: get(filtersFromProps, 'end_date'),
                ou: get(filtersFromProps, 'ou'),
              },
            })
          } else {
            setHasFetchError(true)
          }
        })
        .catch(() => {
          setHasFetchError(true)
        })
    }
  }

  useEffect(() => {
    fetchData(1)
  }, [ou])

  useEffect(() => {
    fetchData(1, filters)
    getRoleChangeRequestFilterOptions().then(options => {
      setFilterOptions(options)
    })
  }, [currentTab])

  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    return dayjs.utc(dateString).local().format('MM/DD/YYYY [at] HH:mm:ss A')
  }

  const formatProcessedDate = (dateString: string) => {
    if (!dateString) return ''
    return dayjs.utc(dateString).local().format('MM/DD/YYYY, HH:mm:ss A')
  }

  const getActiveFiltersCount = () => {
    return Object.values(filters || {}).filter(v => v !== undefined && v !== '' && v !== null).length
  }

  const approveRequest = (requestId: string) => {
    approveRoleChangeRequest(requestId)
      .then(res => {
        if (res?.ok) {
          fetchData(page)
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'Experimenter access request approved successfully', severity: 'success' },
          })
        } else {
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'Failed to accept the request. Please try again later.', severity: 'warning' },
          })
        }
      })
      .catch(err => {
        appStateContext?.dispatch({
          type: 'SET_SNACKBAR_STATE',
          payload: { message: 'Failed to accept the request. Please try again later.', severity: 'warning' },
        })
      })
  }

  const declineRequest = (requestId: string, notes: string) => {
    declineRoleChangeRequest(requestId, notes)
      .then(res => {
        if (res?.ok) {
          fetchData(page)
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'Experimenter access request declined successfully', severity: 'success' },
          })
        } else {
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'Failed to declined the request. Please try again later.', severity: 'warning' },
          })
        }
      })
      .catch(err => {
        appStateContext?.dispatch({
          type: 'SET_SNACKBAR_STATE',
          payload: { message: 'Failed to decline the request. Please try again later.', severity: 'warning' },
        })
      })
    setIsDeclineDialogOpen(false)
    setSelectedRequest(null)
    setDeclinedNotes('')
  }

  useEffect(() => {
    if (isEmpty(data) && page > 1) {
      fetchData(page - 1, filters)
    }
  }, [data])

  const getTableData = useMemo(() => {
    return currentTab === 'pending'
      ? data.map(item => {
          const pendingItem = item as PendingRequest
          return {
            hasBackground: false,
            cell: [
              <div>
                <p className={styles.name}>{pendingItem.user.name}</p>
                <p className={styles.email}>{pendingItem.user.email}</p>
              </div>,
              <p className={styles.ou}>{toUpper(pendingItem.ou)}</p>,
              <p className={styles.function}>{pendingItem.function}</p>,
              <Button
                className={styles.requestReasonButton}
                onClick={() => {
                  setSelectedRequest(pendingItem)
                  setIsDetailsDialogOpen(true)
                }}>
                Details
              </Button>,
              <p className={styles.requested}>{formatDate(pendingItem.created_at)}</p>,
              <div className={styles.actions}>
                <Button
                  className={styles.approveButton}
                  onClick={() => approveRequest(pendingItem.id)}>
                  <img
                    src={AcceptIcon}
                    alt="Accept"
                  />
                  Accept
                </Button>
                <Button
                  className={styles.declineButton}
                  onClick={() => {
                    setSelectedRequest(pendingItem)
                    setIsDeclineDialogOpen(true)
                  }}>
                  <img
                    src={DeclineIcon}
                    alt="Decline"
                  />
                  Decline
                </Button>
              </div>,
            ],
          }
        })
      : data.map(item => {
          const processedItem = item as ProcessedRequest
          return {
            hasBackground: false,
            cell: [
              <div>
                <p className={styles.name}>{processedItem.user.name}</p>
                <p className={styles.email}>{processedItem.user.email}</p>
              </div>,
              <Button
                className={styles.requestReasonButton}
                onClick={() => {
                  setSelectedRequest(processedItem)
                  setIsDetailsDialogOpen(true)
                }}>
                Details
              </Button>,
              <Chip
                className={processedItem.status === 'rejected' ? styles.declinedStatus : styles.approvedStatus}
                label={processedItem.status === 'rejected' ? 'Declined' : 'Approved'}
                avatar={
                  processedItem.status === 'rejected' ? (
                    <img
                      alt="Declined"
                      src={DeclinedIcon}
                      className={styles.declinedIcon}
                    />
                  ) : (
                    <CheckCircle className={styles.checkIcon} />
                  )
                }
              />,
              <p className={styles.notes}>{isEmpty(processedItem.notes) ? '-' : processedItem.notes}</p>,
              <div>
                <p className={styles.name}>{processedItem.processed_by.name}</p>
                <p className={styles.email}>{processedItem.processed_by.email}</p>
              </div>,
              <div>
                <p className={styles.processedDate}>{formatProcessedDate(processedItem.updated_at)}</p>
                <p className={styles.requestedDate}>{`Requested: ${formatDate(processedItem.created_at)}`}</p>
              </div>,
            ],
          }
        })
  }, [data])

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.headerText}>Access Request Management</h1>
        <p className={styles.headerDescription}>Manage access requests and monitor approval history</p>
      </div>
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          className={styles.tabContainer}>
          <Tabs
            value={currentTab}
            onChange={(_, newValue: 'pending' | 'history') => setCurrentTab(newValue)}
            classes={{ indicator: styles.tabIndicator }}>
            <Tab
              className={styles.tab}
              label="Pending Requests"
              value="pending"
            />
            <Tab
              className={styles.tab}
              label="Requests History"
              value="history"
            />
          </Tabs>
        </Box>
      </Box>
      <div className={styles.subHeaderContainer}>
        <div>
          <h1 className={styles.contentTitleText}>
            {currentTab === 'pending' ? 'Pending Access Requests' : 'Request History'}
          </h1>
          <p className={styles.contentDescription}>
            {currentTab === 'pending'
              ? 'Review and approve or decline pending requests'
              : 'View all processed requests and their approval history'}
          </p>
        </div>
        {currentTab === 'history' ? (
          <Badge
            badgeContent={getActiveFiltersCount()}
            invisible={getActiveFiltersCount() === 0}
            classes={{ badge: styles.filtersButtonBadge }}>
            <Button
              variant="contained"
              endIcon={<Sort />}
              className={styles.filtersButton}
              onClick={() => setIsFiltersDialogOpen(true)}>
              <p className={styles.buttonText}>Filters</p>
            </Button>
          </Badge>
        ) : (
          <FormControl size="small">
            {isEmpty(ou) && <InputLabel shrink={false}>All</InputLabel>}
            <Select
              size="small"
              value={ou}
              onChange={(e: SelectChangeEvent) => {
                setOu(e.target.value)
              }}
              className={styles.ouSelect}>
              <MenuItem
                key={'no-ou'}
                value={undefined}>
                All
              </MenuItem>
              {map(filterOptions?.ous, option => (
                <MenuItem
                  key={option}
                  value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>

      <div className={styles.tableContainer}>
        {data.length > 0 ? (
          <CustomTable
            headTitles={headTitles}
            data={getTableData}
            onRequestSort={() => {}}
            order={undefined}
            orderBy={undefined}
            onPageChange={page => {
              fetchData(page)
            }}
            page={page}
            total={getTotalPages()}
          />
        ) : (
          <div className={styles.noExperimentsContainer}>
            {hasFetchError ? <img src={FetchError} /> : <img src={EmptyBox} />}
            <p className={styles.noExperimentsHeader}>
              {hasFetchError ? 'Error Fetching the Requests' : 'No record to display at the moment'}
            </p>
            {hasFetchError && (
              <p className={styles.noExperimentsText}>
                There was an issue retrieving the requests. Check your connection and try again. If the error persists,
                contact the support team.
              </p>
            )}
          </div>
        )}
      </div>
      <CustomDialog
        open={isDetailsDialogOpen}
        setClose={() => {
          setIsDetailsDialogOpen(false)
          setSelectedRequest(null)
        }}
        title="Reason to request the access "
        isRequestDialog
        isBoldTitle
        hasDivider={false}
        isRoboto>
        <div>
          <p className={styles.requestReasonText}>{selectedRequest?.request_reason}</p>
        </div>
      </CustomDialog>
      <CustomDialog
        open={isDeclineDialogOpen}
        title="Decline Experimenter request"
        primaryAction={() => declineRequest(selectedRequest?.id ?? '', declinedNotes)}
        primaryActionButtonText="Decline"
        secondaryAction={() => {
          setSelectedRequest(null)
          setIsDeclineDialogOpen(false)
        }}
        secondaryActionButtonText="Cancel"
        isRequestDialog
        isBoldTitle
        hasDivider={false}
        isRoboto>
        <div className={styles.declineDialogContainer}>
          <TextField
            multiline
            rows={2}
            label="Leave a note (optional)"
            slotProps={{
              inputLabel: {
                shrink: true,
              },
              formHelperText: {
                className: styles.experimenterRequestDialogHelperText,
              },
              htmlInput: { maxLength: 500 },
            }}
            value={declinedNotes}
            onChange={e => setDeclinedNotes(e.target.value)}
            helperText={`${declinedNotes.length}/500`}
          />
        </div>
      </CustomDialog>
      <FiltersDialog
        open={isFiltersDialogOpen}
        primaryAction={async (filters: object) => {
          setIsFiltersDialogOpen(false)
          await fetchData(1, filters)
        }}
        primaryActionButtonText="Apply Filters"
        secondaryAction={() => {
          setIsFiltersDialogOpen(false)
        }}
        secondaryActionButtonText="Cancel"
        title="Filters"
        filters={filters as RequestHistoryFilters}
        filterOptions={filterOptions}
      />
    </div>
  )
}

export default AccessRequestManagement
